/**
 * Compose / sync structured name fields on Player profile.
 * Competitions require first + last on Entry snapshots; profile stores both + displayName.
 */

import {
  isRallyAllyAccountEmail,
  rallyAllyUserIdFromEmail,
} from "../auth/rallyAllyAuthEmail";

/**
 * @param {string | null | undefined} firstName
 * @param {string | null | undefined} lastName
 * @returns {string}
 */
export function composeNameFromParts(firstName, lastName) {
  return [firstName, lastName]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ");
}

/**
 * Prefer explicit displayName; otherwise First + Last.
 * @param {{ firstName?: string | null, lastName?: string | null, displayName?: string | null }} parts
 * @returns {string}
 */
export function resolvePlayerDisplayName(parts = {}) {
  const explicit = String(parts.displayName || "").trim().replace(/\s+/g, " ");
  if (explicit) return explicit;
  return composeNameFromParts(parts.firstName, parts.lastName);
}

/**
 * True when a label is the RallyAlly login id (or internal account email), not a person name.
 * @param {string | null | undefined} value
 * @param {string | null | undefined} [accountEmail] Auth/profile email for comparison
 */
export function isAccountLoginLabel(value, accountEmail = null) {
  const s = String(value || "").trim();
  if (!s) return false;
  if (isRallyAllyAccountEmail(s)) return true;
  const fromEmail = rallyAllyUserIdFromEmail(accountEmail);
  if (fromEmail && fromEmail.toLowerCase() === s.toLowerCase()) return true;
  return false;
}

/** Placeholder written when no profile name exists yet. */
function isGenericPlayerLabel(value) {
  return String(value || "").trim().toLowerCase() === "player";
}

/**
 * Name to store on a new RallyAlly player.
 * A typed user id (`aku_loc_browser`) wins over the `"Player"` placeholder.
 * A real name hint (Google display name, profile edit) is kept.
 * @param {string | null | undefined} displayNameHint
 * @param {string | null | undefined} [accountEmail]
 * @returns {string}
 */
export function resolveAccountPlayerName(displayNameHint, accountEmail) {
  const loginId = rallyAllyUserIdFromEmail(accountEmail);
  const hint = String(displayNameHint || "").trim();
  const fromHintEmail = rallyAllyUserIdFromEmail(hint);
  if (fromHintEmail) return fromHintEmail;
  if (hint && !isGenericPlayerLabel(hint) && !isRallyAllyAccountEmail(hint)) return hint;
  if (loginId) return loginId;
  return "Player";
}

/**
 * Replace a placeholder player name with the account user id or another real hint.
 * Leaves a name the player already saved.
 * @param {{ displayName?: string | null, firstName?: string | null, lastName?: string | null } | null | undefined} player
 * @param {string | null | undefined} preferredName
 * @returns {Record<string, string>}
 */
export function placeholderPlayerNamePatch(player, preferredName) {
  const preferred = String(preferredName || "").trim();
  /** @type {Record<string, string>} */
  const patch = {};
  const current = String(player?.displayName || "").trim();
  let nextName = current;

  if (isRallyAllyAccountEmail(current)) {
    const id = rallyAllyUserIdFromEmail(current);
    if (id) nextName = id;
  } else if (
    (!current || isGenericPlayerLabel(current)) &&
    preferred &&
    !isGenericPlayerLabel(preferred)
  ) {
    nextName = preferred;
  }

  if (nextName && nextName !== current) patch.displayName = nextName;
  if (!patch.displayName) return patch;

  const first = String(player?.firstName || "").trim();
  if (!first || isGenericPlayerLabel(first)) {
    const parts = splitDisplayNameParts(patch.displayName);
    if (parts.firstName) patch.firstName = parts.firstName;
    const last = String(player?.lastName || "").trim();
    if (!last && parts.lastName) patch.lastName = parts.lastName;
  }
  return patch;
}

/**
 * @param {...(string | null | undefined)} values
 */
function loginIdFromValues(...values) {
  for (const raw of values) {
    const fromEmail = rallyAllyUserIdFromEmail(raw);
    if (fromEmail) return fromEmail;
  }
  return "";
}

/**
 * Compact person label for roster rows (given name when multi-word).
 * @param {string} value
 */
function compactPersonLabel(value) {
  const s = String(value || "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  if (/\s/.test(s)) return s.split(/\s+/)[0];
  return s;
}

/**
 * Arena Players roster label.
 *
 * Priority:
 * 1. Profile name (`firstName`, else profile display / First+Last) when set
 * 2. Google / Firebase Auth displayName
 * 3. RallyAlly userid (`bravo` from `bravo@account.therallyally.com`)
 * 4. `"Player"`
 *
 * @param {{ firstName?: string | null, lastName?: string | null, displayName?: string | null }} [player]
 * @param {string | null | undefined} [fallback]
 * @param {{
 *   accountEmail?: string | null,
 *   authUser?: { displayName?: string | null, email?: string | null, providerData?: Array<{ providerId?: string }> } | null,
 * }} [opts]
 * @returns {string}
 */
export function resolveArenaRosterLabel(player = {}, fallback = "", opts = {}) {
  const authUser = opts.authUser || null;
  const accountEmail = opts.accountEmail || authUser?.email || null;

  // 1) Profile first name (structured)
  const first = String(player?.firstName || "").trim();
  if (first && !isGenericPlayerLabel(first)) return first;

  // 1b) Other profile name fields (displayName or First+Last) — not userid / placeholder
  const profileName = resolvePlayerDisplayName({
    firstName: player?.firstName,
    lastName: player?.lastName,
    displayName: player?.displayName,
  });
  if (
    profileName &&
    !isGenericPlayerLabel(profileName) &&
    !isAccountLoginLabel(profileName, accountEmail) &&
    !isRallyAllyAccountEmail(profileName)
  ) {
    return compactPersonLabel(profileName);
  }

  // 2) Google / Auth display name
  const authName = String(authUser?.displayName || "").trim().replace(/\s+/g, " ");
  if (
    authName &&
    !isGenericPlayerLabel(authName) &&
    !isRallyAllyAccountEmail(authName) &&
    !isAccountLoginLabel(authName, accountEmail)
  ) {
    return compactPersonLabel(authName);
  }

  const fallbackName = String(fallback || "").trim().replace(/\s+/g, " ");
  if (
    fallbackName &&
    !isGenericPlayerLabel(fallbackName) &&
    !isRallyAllyAccountEmail(fallbackName) &&
    !isAccountLoginLabel(fallbackName, accountEmail)
  ) {
    return compactPersonLabel(fallbackName);
  }

  // 3) Userid / password accounts — local-part of @account.therallyally.com
  const loginId = loginIdFromValues(
    accountEmail,
    player?.displayName,
    authUser?.email,
    fallbackName,
    authName,
    profileName
  );
  if (loginId) return loginId;

  // Bare userid already stored as displayName (same as login id)
  for (const raw of [player?.displayName, authName, fallbackName]) {
    const value = String(raw || "").trim();
    if (!value || isGenericPlayerLabel(value)) continue;
    if (isRallyAllyAccountEmail(value)) {
      const id = rallyAllyUserIdFromEmail(value);
      if (id) return id;
      continue;
    }
    return compactPersonLabel(value);
  }

  return "Player";
}

/**
 * Split a legacy display name into first / last when structured fields are missing.
 * @param {string} displayName
 * @returns {{ firstName: string, lastName: string }}
 */
export function splitDisplayNameParts(displayName) {
  const parts = String(displayName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}
